import {AttrsSchema, Component, ConfigOptions, jsx} from 'panel';
import isEqual from 'lodash/isEqual';
import isNumber from 'lodash/isNumber';

import {TIMEZONES} from 'common/constants';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {FunnelQueryResponse} from 'common/report/funnels/models/api-types';
import {ColorChoice, FunnelBookmark, FunnelMedianDataSegment, FunnelSegment} from 'common/report/funnels/models/types';
import {FunnelsQuery} from 'common/report/funnels/models/funnels-query';
import {
  getMedianAccessor,
  getMedianFormatter,
  getTTCShiftedEventIndex,
} from 'common/report/funnels/models/time-to-convert/utils';
import {generateMedianSegments, processFunnelForChartWidget} from 'common/report/funnels/models/aggregates';
import {ChartsContextRegistry} from 'common/charts/contexts/registry';
import {SegmentFilterController} from 'common/charts/contexts/segment-filter-controller';
import {ChartType, REPORT_LIMITS, ReportName} from 'common/report/constants';
import {MultiMetricChartEl} from 'common/charts/visualization/multi-metric';
import {TwoStepDiagramEl} from 'common/charts/misc/two-step-diagram';
import {getBookmarkWithAppliedDefaults} from 'common/report/funnels/util/bookmark-overrides';
import {CurrentProject, CurrentProjectResource} from 'common/report/model-resources/projects';

import css from './index.inline.styl';
import {getDataTableColumns, getDataTableRows} from './util';

interface TTCAggregateMetricAttrs {
  'project-as-of-date': string;
}

type ProjectDetails = {
  projectId: string;
  projectAsOfDate: null | string;
  projectTimeZone: typeof TIMEZONES[number];
};
export interface FunnelsTTCAggregateMetricJsxProps {
  props: {
    bookmark: FunnelBookmark;
    funnelsResponse: FunnelQueryResponse;

    projectDetails?: ProjectDetails | null;
  };
}

interface TTCAggregateMetricState {
  formattedData: FunnelSegment[];
  formattedCompareData: FunnelSegment[];
  selectedSegments: ColorChoice[];
  stepIndex: number;
  query: FunnelsQuery;
  dateRangeLabels: string[];
  medianDataSegments: FunnelMedianDataSegment[];
}

export class FunnelsTTCAggregateMetric extends Component<
  TTCAggregateMetricState,
  TTCAggregateMetricAttrs,
  any,
  any,
  ChartsContextRegistry
> {
  private _funnelsResponse: FunnelQueryResponse;
  private _bookmark: FunnelBookmark;
  private _projectDetails: ProjectDetails | null = null;

  static get attrsSchema(): AttrsSchema<TTCAggregateMetricAttrs> {
    return {
      ...super.attrsSchema,
      'project-as-of-date': `string`,
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this.processData();
  }

  resourceCustomSubscriptions = new Map([[CurrentProjectResource, () => !this._projectDetails && this.processData()]]);

  private getCurrentProject(): CurrentProject {
    return this.getContext(`currentProject`).get().value;
  }

  private get segmentFilterController(): SegmentFilterController {
    return this.getContext(`segmentFilterController`);
  }

  get config(): ConfigOptions<TTCAggregateMetricState, any, ChartsContextRegistry> {
    return {
      useShadowDom: true,
      css,
      contexts: [`segmentFilterController` as const, `currentProject` as const],
      defaultContexts: {
        segmentFilterController: new SegmentFilterController(),
      },
      template: () => this.renderTemplate(),
    };
  }

  public set funnelsResponse(response: FunnelQueryResponse) {
    if (!isEqual(this._funnelsResponse, response)) {
      this._funnelsResponse = response;
      this.processData();
    }
  }

  public set bookmark(bookmark: FunnelBookmark) {
    const updatedBookmark = getBookmarkWithAppliedDefaults(bookmark);
    if (!isEqual(this._bookmark, updatedBookmark)) {
      this._bookmark = updatedBookmark;
      this.processData();
    }
  }

  public set projectDetails(projectDetails: ProjectDetails) {
    this._projectDetails = projectDetails;
  }

  private getProjectDetails(): null | ProjectDetails {
    if (this._projectDetails) {
      return this._projectDetails;
    }
    const currentProject = this.getCurrentProject();
    if (currentProject) {
      return {
        projectId: currentProject.id.toString(),
        projectAsOfDate: currentProject.projectAsOfDate,
        projectTimeZone: currentProject.timezone,
      };
    }
    return null;
  }

  private hasRequiredParams() {
    return this._funnelsResponse && this._bookmark && this.isConnected && this.getProjectDetails();
  }

  private processData() {
    if (!this.hasRequiredParams()) {
      return;
    }

    const stepIndex = getTTCShiftedEventIndex(this._bookmark.eventIndex);

    const medianAccessor = getMedianAccessor(stepIndex);
    const projectDetails = this.getProjectDetails();
    const {legendSegmentItems, stateUpdates, savedLegend} = processFunnelForChartWidget(
      this._bookmark,
      this._funnelsResponse,
      projectDetails.projectId,
      projectDetails.projectAsOfDate,
      stepIndex,
      medianAccessor,
      projectDetails.projectTimeZone,
    );

    const {formattedData, formattedCompareData} = stateUpdates;
    const hasComparisonData = !!formattedCompareData;

    const medianDataSegments = generateMedianSegments({
      formattedData,
      formattedCompareData,
      medianAccessor,
      hasComparisonData,
      stepIndex,
    });

    const rows = getDataTableRows(medianDataSegments);
    const tableColumns = getDataTableColumns(this._bookmark, hasComparisonData);

    const segmentItemLookup = new Map(legendSegmentItems.map((item) => [item.id, item]));
    this.segmentFilterController.loadSections(
      [
        {
          items: rows.map((tableRow) => ({...segmentItemLookup.get(tableRow.identifier), tableRow})),
          tableColumns,
        },
      ],
      {
        defaultNumSelections: REPORT_LIMITS[ReportName.Funnels].segments[ChartType.FunnelMedianTTC].default,
        maxNumSelections: REPORT_LIMITS[ReportName.Funnels].segments[ChartType.FunnelMedianTTC].max,
        tableSortConfig: this._bookmark.sorting,
      },
    );

    this.segmentFilterController.loadFromSavedLegend(savedLegend);

    this.update({
      ...stateUpdates,
      medianDataSegments,
    });
  }

  private renderTemplate() {
    if (!this.hasRequiredParams() || !this.state.medianDataSegments) {
      return <div sel='.root' />;
    }

    const medianSegmentLookup = new Map(this.state.medianDataSegments.map((data) => [data.prop, data]));
    const metrics = this.segmentFilterController.getSelectedItems(0).map((item) => {
      const {prop, value, label, comparisonValue} = medianSegmentLookup.get(item.id);
      return {
        label,
        value,
        subLabel: `Median Time to Convert`,
        comparisonValue,
        color: this.segmentFilterController.getItemColor(0, prop),
      };
    });

    return (
      <div sel='.root'>
        <TwoStepDiagramEl attrs={{elref: `stepsDiagram`, 'step-names': this.getSelectedStepNames()}} />
        <MultiMetricChartEl attrs={{elref: `multiMetric`, metrics}} props={{metricFormatter: getMedianFormatter}} />
      </div>
    );
  }

  private getSelectedStepNames(): [string, string] {
    if (!this.state.query) {
      return [``, ``];
    }

    const {eventIndex} = this._bookmark;
    const stepLabels = this.state.query.getStepLabels();

    if (isNumber(eventIndex)) {
      const [step1, step2] = stepLabels.slice(eventIndex, eventIndex + 2);
      return [`${eventIndex + 1}. ${step1}`, `${eventIndex + 2}. ${step2}`];
    }

    return [`1. ${stepLabels[0]}`, `${stepLabels.length}. ${stepLabels[stepLabels.length - 1]}`];
  }
}

export const FunnelsTtcAggregateMetricWidgetEl = defineAndMakeJsxFactory(
  `mp-funnels-ttc-aggregate-metric-widget`,
  FunnelsTTCAggregateMetric,
);
export const FunnelsTtcAggregateMetricWidgetReact = panel2react(
  `mp-funnels-ttc-aggregate-metric-widget`,
  FunnelsTTCAggregateMetric,
);
